import React, { FC } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";

import { Login } from "pages/Login";
import { useAppState } from "context";
import { Dashboard } from "pages/Dashboard";
import { Reports } from "pages/Reports";
import { AssignOrganizations } from "pages/AssignOrganizations";
import { AssignUsers } from "pages/AssignUsers";
import { Report } from "pages/Report";
import { UserRoles } from "pages/UserRoles";
import { ManageDepartments } from "pages/ManageDepartments";
import { ManageOrganizations } from "pages/ManageOrganizations";
import { AddOrEditUser } from "pages/AddOrEditUser";
import { AddOrEditDepartmentUsers } from "pages/AddOrEditDepartmentUsers";
import { Statistics } from "pages/Statistics";
import { CreatePassword } from "pages/CreatePassword";
import { TwoFA } from "pages/TwoFA";
import { UserInformation } from "pages/UserInformation";
import { Feedback } from "pages/Feedback";
import { AddOrEditOrganizationUsers } from "pages/AddOrEditOrganizationUsers";
import { LocationStatistics } from "pages/LocationStatistics";

// note: these key names matter to LeftNav component
export const routePaths = {
  home: "/",
  dashboard: "/dashboard",
  login: "/login",
  report: "/report/:reportId",
  assignOrganization: "/report/:reportId/assignOrganization",
  assignUser: "/report/:reportId/assignUser",
  reports: "/reports",
  userRoles: "/userRoles",
  manageDepartments: "/manageDepartments",
  manageOrganizations: "/manageOrganizations",
  addOrganizationsUsers: "/addOrganizationsUsers",
  editOrganizationsUsers: "/editOrganizationsUsers/:editOrganizationId",
  addUser: "/addUser",
  editUser: "/editUser/:editUserId",
  addDepartmentUsers: "/addDepartmentUsers",
  editDepartmentUsers: "/editDepartmentUsers/:editDepartmentId",
  statistics: "/statistics",
  passwordReset: "/passwordReset/:resetKey",
  twoFA: "/twoFA",
  userInformation: "/userInformation",
  feedback: "/feedback",
  locationStatistics: "/locationStatistics",
};

const AuthenticatedRoutes = () => {
  return (
    <Routes>
      <Route path={routePaths.assignOrganization} element={<AssignOrganizations />} />
      <Route path={routePaths.addOrganizationsUsers} element={<AddOrEditOrganizationUsers />} />
      <Route path={routePaths.editOrganizationsUsers} element={<AddOrEditOrganizationUsers />} />
      <Route path={routePaths.assignUser} element={<AssignUsers />} />
      <Route path={routePaths.report} element={<Report />} />
      <Route path={routePaths.reports} element={<Reports />} />
      <Route path={routePaths.userRoles} element={<UserRoles />} />
      <Route path={routePaths.manageDepartments} element={<ManageDepartments />} />
      <Route path={routePaths.manageOrganizations} element={<ManageOrganizations />} />
      <Route path={routePaths.addUser} element={<AddOrEditUser />} />
      <Route path={routePaths.editUser} element={<AddOrEditUser />} />
      <Route path={routePaths.addDepartmentUsers} element={<AddOrEditDepartmentUsers />} />
      <Route path={routePaths.editDepartmentUsers} element={<AddOrEditDepartmentUsers />} />
      <Route path={routePaths.statistics} element={<Statistics />} />
      <Route path={routePaths.locationStatistics} element={<LocationStatistics />} />
      <Route path={routePaths.dashboard} element={<Dashboard />} />
      <Route path={routePaths.userInformation} element={<UserInformation />} />
      <Route path={routePaths.feedback} element={<Feedback />} />
      <Route path={routePaths.home} element={<Dashboard />} />
    </Routes>
  );
};

const AuthGate: FC = () => {
  const appState = useAppState();
  const location = useLocation();

  if (appState.Authenticating && !appState.Authenticated) {
    return null;
  }
  if (appState.Authenticated) {
    return <AuthenticatedRoutes />;
  }
  return <Navigate to={routePaths.login} state={{ from: location }} replace />;
};

export const Routing: FC = () => {
  return (
    <Router>
      <Routes>
        <Route path={routePaths.login} element={<Login />} />
        <Route path={routePaths.passwordReset} element={<CreatePassword />} />
        <Route path={routePaths.twoFA} element={<TwoFA />} />
        <Route path="*" element={<AuthGate />} />
      </Routes>
    </Router>
  );
};
